import React from 'react';
import { EVENT_SIZE_BANDS, type EventSize } from '../utils/eventSize';

/**
 * SAM-592 — event price matrix: rows = booking options, columns = event size bands. Each cell is
 * the FLAT price of the whole event at that size (not per ticket). An empty cell = the option is
 * not sold at that size (the backend rejects it with event_size_price_missing).
 * Tailwind + tokens.css only (BaseUI/Styletron freeze, SAM-175).
 */

export interface EventPricingOptionRow {
  index: number;
  id: string;
  label: string;
  pricesByEventSize: Record<string, string>;
}

interface Props {
  options: EventPricingOptionRow[];
  onCellChange: (optionIndex: number, size: EventSize, value: string) => void;
}

export const ServiceEventPricingMatrix: React.FC<Props> = ({ options, onCellChange }) => (
  <div className="text-sm">
    <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
      Events are charged by size, not per ticket. Set the flat price of the whole event for each
      size band; the size is the total number of guests in the booking. Leave a cell empty to not
      sell that size.
    </p>
    {options.length === 0 ? (
      <p className="text-xs italic" style={{ color: 'var(--ink-400)' }}>
        Add a booking option above to set its event prices.
      </p>
    ) : (
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--line)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ background: 'var(--surface-2)' }}>
              <th className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-500)' }}>Option</th>
              {EVENT_SIZE_BANDS.map((band) => (
                <th key={band.key} className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>
                  <div>{band.key.toUpperCase()}</div>
                  <div className="font-normal text-[11px]" style={{ color: 'var(--ink-400)' }}>{band.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option.index} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                <td className="px-2 py-2 align-top" style={{ color: 'var(--ink-900)' }}>
                  {option.label || option.id || '(unnamed)'}
                </td>
                {EVENT_SIZE_BANDS.map((band) => (
                  <td key={band.key} className="px-2 py-2 align-top">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      aria-label={`${option.label || option.id} price for ${band.label}`}
                      value={option.pricesByEventSize[band.key] ?? ''}
                      placeholder="—"
                      onChange={(e) => onCellChange(option.index, band.key, e.target.value)}
                      className="w-24 rounded px-1.5 py-1 text-xs"
                      style={{ border: '1px solid var(--line)' }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
